import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Chrome } from "lucide-react";
import { storeUrl } from "../../lib/links";
import { isPhone } from "../../lib/device";
import { getTryCopy } from "./copy";
import { HeroFilm } from "../../components/HeroFilm";
import { PhoneInstallForm } from "./PhoneInstallForm";
import { BUTTON_CLASSES } from "./styles";

// Outbound destination page. Deliberately one screen with a single action: no
// navbar, no pricing, no footer links, nothing competing with the install.
// Noindexed at the edge too (see vercel.json), this meta tag is belt and braces
// for anything that renders the SPA before hitting our headers.
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);
}

const STEPS = [
  { n: "1", label: "Install the extension" },
  { n: "2", label: "Connect a tool your team already uses" },
  { n: "3", label: "Context shows up in your next meeting" },
];

export default function TryPage() {
  const [searchParams] = useSearchParams();
  const { headline, subhead } = getTryCopy(searchParams.get("p"));
  const phone = isPhone(navigator);

  useNoIndex();

  useEffect(() => {
    document.title = "Try Intakall";
  }, []);

  return (
    <main className="flex min-h-screen flex-col justify-center bg-paper bg-grid-paper bg-grid px-4 py-14 font-sans text-char sm:px-6 sm:py-20">
      <div className="mx-auto flex max-w-3xl flex-col items-center gap-7 text-center">
        <img src="/mark.svg" alt="Intakall" className="h-10 w-10" />

        <h1 className="text-balance font-serif text-[clamp(2.4rem,5vw,3.6rem)] font-medium leading-[0.98] tracking-[-0.02em] text-char">
          {headline}
        </h1>

        <p className="max-w-2xl text-lg leading-relaxed text-char/80">
          {subhead}
        </p>

        {phone ? (
          <PhoneInstallForm refTag={searchParams.get("ref")} />
        ) : (
          <>
            <a
              href={storeUrl("try")}
              target="_blank"
              rel="noopener noreferrer"
              className={BUTTON_CLASSES}
            >
              <Chrome className="h-4 w-4" aria-hidden="true" />
              Add to Chrome
            </a>

            <p className="font-hand text-[22px] text-char/75">
              Free to install, no card. Works in Chrome, Edge, and Brave.
            </p>
          </>
        )}

        <div className="mt-4 w-full">
          <HeroFilm layout="column" />
        </div>

        {/* Setup expectations. Installing alone shows only the transcript, so
            saying "connect a tool" here is what stops someone concluding that
            the transcript is the whole product. */}
        <ol
          data-testid="try-steps"
          className="mt-2 flex w-full flex-col gap-3 text-left sm:flex-row sm:justify-center sm:gap-8"
        >
          {STEPS.map((step) => (
            <li key={step.n} className="flex items-start gap-3 text-sm text-char/80 sm:max-w-[15rem] sm:flex-1">
              <span
                aria-hidden="true"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-char bg-paper-white text-xs font-semibold text-char"
              >
                {step.n}
              </span>
              {step.label}
            </li>
          ))}
        </ol>
      </div>
    </main>
  );
}
